import { type FC } from 'react'
import styled from 'styled-components'
import { LANG_COUNTRY } from '@/utils'
import { E_FILTER_TYPE, type TypeCmsProduct } from '@/schema'
import { mq, presets } from '@/styles'
import { Img } from '@/components/base'

type Props = {
  labels?: TypeCmsProduct['labels']
  filters?: TypeCmsProduct['filters']
  frozen?: boolean
  discount?: number
  displayedIn?: 'card' | 'pdp' | 'cartline'
}

export const TRANSLATIONS_PRODUCT_TAGS = {
  'es-ES': 'Etiquetas del producto',
  'en-GB': 'Product tags',
  'fr-FR': 'Étiquettes du produit',
  'de-DE': 'Produkt-Tags',
  'pt-PT': 'Etiquetas do produto',
  'it-IT': 'Tag del prodotto'
} as const

const getLabels = (data:Props['labels']) => data?.map((item, idx) => {
  return <li key={`label-${item.id}-${idx}`}>{item.name}</li>
})

const getIconFilters = (data:Props['filters']) => {
  if(!data?.length) return null
  const arr = data.filter(item => (item.type === E_FILTER_TYPE.BOOLEAN && item.showAsLabel))

  return arr.map((item, idx) => {
    return <li key={`label-${idx}-icon`} aria-label={item.name}><Img media={item.icon} /></li>
  })
}

export const Tags:FC<Props> = ({ labels = [], filters = [], frozen = false, discount }) => {
  const Labels = getLabels(labels)
  const IconFilters = getIconFilters(filters)
  const hasDiscount = typeof discount === 'number' && discount !== 0
  return (
    <Ul aria-label={TRANSLATIONS_PRODUCT_TAGS[LANG_COUNTRY]} className='tags'>
      {Labels}
      {IconFilters}
      {hasDiscount && <li className={`discount ${frozen ? ' frozen' : ''}`}>{discount}%</li>}
      {frozen && <li aria-label='frozen'>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src='/images/svg/label-frozen.svg' alt='Icon: frozen'/>
      </li>}
    </Ul>
  )
}

const Ul = styled.ul`
  align-items: start;
  display: grid;
  gap: 2px;
  grid-auto-columns: max-content;
  grid-auto-flow: row dense;
  grid-template-columns: auto;
  left: 0;
  padding: 8px;
  pointer-events: none;
  position: absolute;
  top: 0;
  user-select: none;
  width: 100%;

  ${mq.greaterThan('tablet')} {
    gap: 3px 4px;
  }

  li {
    align-items: center;
    background-color: var(--color-gold);
    border-radius: 12px;
    color: var(--color-white);
    display: flex;
    ${presets.font12_1()}
    grid-column: 1;
    grid-row: auto;
    justify-content: center;
    padding: 3px 6px;
    overflow: hidden;
    text-overflow: ellipsis;
    text-transform: uppercase;
    user-select: none;
    white-space: nowrap;
    width: fit-content;

    ${mq.greaterThan('tablet')} {
      padding: 5px 7px;
    }

    &:first-child {
      grid-column: 1;
      grid-row: 1;
    }

    &[aria-label],
    &.discount {
      grid-column: auto;
      grid-row: 1;
      justify-self: end;
    }

    &[aria-label] {
      background-color: transparent;
      padding: 0;
      width: fit-content;

      img {
        height: 16px;
        width: 16px;

        ${mq.greaterThan('tablet')} {
          height: 18px;
          width: 18px;
        }
      }
    }

    &.discount {
      background-color: var(--color-text);

      &.frozen {
        background-color: var(--color-card-text--frozen);
      }
    }
  }
`


export const FILTER_LABELS = {
  'sin-gluten': '/images/svg/label-sin-gluten.svg',
  'sans-gluten': '/images/svg/label-sin-gluten.svg',
  'gluten-free': '/images/svg/label-sin-gluten.svg',
  bio: '/images/svg/label-bio.svg',
  organic: '/images/svg/label-organic.svg',
  integral: '/images/svg/label-integral.svg',
  wholegrain: '/images/svg/label-integral.svg',
  vegano: '/images/svg/label-vegan.svg',
  vegan: '/images/svg/label-vegan.svg',
  vegetalien: '/images/svg/label-vegan.svg',
  frozen: '/images/svg/label-frozen.svg',
}
